import { useClient } from '@cortex/react-query';
import { extractSignalKeyVars, validateSignalKeyTemplate } from '@cortex/types';
import type {
  Workflow,
  WorkflowDedupSettingsResponse,
  WorkflowInputSchemaResponse,
  WorkflowOutputSchemaResponse,
} from '@cortex/types';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { InfoHint } from '../common/InfoHint';
import { JsonSchemaBuilder } from '../common/JsonSchemaBuilder';
import { JsonataMappingBuilder } from '../common/JsonataMappingBuilder';
import { Spinner } from '../common/Spinner';

import {
  fromMappingRows,
  MappingRow,
  mappingSmallBtn,
  toMappingRows,
  topLevelKeys,
  type IConnectSource,
  type IMappingRow,
} from './MappingRow';
import { useWorkflowEditor } from './WorkflowEditorContext';
import { WorkflowInputSchemaView } from './WorkflowInputSchemaView';
import { getEffectiveOutputFieldNamesByNodeKey } from './node-configs/effective-output-schema';
import { compileJsonataObject, parseJsonataObject } from './node-configs/jsonata-object-mapping';
import { invalidateWorkflowQueries } from './workflow-invalidations';

const sectionStyle = { display: 'flex', flexDirection: 'column' as const, gap: 'var(--sp-2)' };
const helpTextStyle = { fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)' };
const errorTextStyle = {
  ...helpTextStyle,
  color: 'var(--status-danger, var(--text-danger))',
};

/**
 * Workflow-level schema editor — collapses what used to be two separate
 * editors (input + output) into a single form with a single Save button
 * at the bottom. Saving cuts ONE new workflow version with all three
 * fields (`inputSchema`, `outputSchema`, `outputMapping`) in one PATCH.
 *
 * - `inputSchema` validates the trigger input on `POST /v1/runs`
 *   (DEV-15893 / EU-26).
 * - `outputSchema` validates `Run.output` at completion (DEV-15894 / EU-27).
 * - `outputMapping` is JSONata over the final `previousOutputs` map used
 *   to compose `Run.output` (DEV-15895 / EU-28). When omitted the runner
 *   falls back to "last terminal node wins".
 */
export function WorkflowSchemaEditor({ workflow }: { workflow: Workflow }) {
  const client = useClient();
  const qc = useQueryClient();
  const editor = useWorkflowEditor();
  const { nodes } = editor;
  const nodeKeys = nodes.map((n) => n.id);

  // Sources offered by the output-mapping editor: the workflow trigger
  // (`__input`) plus every node in the graph. At workflow-output
  // composition time the executor's context is `{ __input,
  // <nodeKey>: <output>, ... }`, so all of these are in scope. We
  // enrich each entry with its declared top-level fields so the
  // Connect-mode field dropdown is populated.
  const outputSources = useMemo<IConnectSource[]>(() => {
    const schemaCtx = {
      nodeTypeOutputSchemaByKey: editor.nodeTypeOutputSchemaByKey,
    };
    const list: IConnectSource[] = [
      {
        sourceKey: '__input',
        label: 'Workflow Input',
        fields: topLevelKeys(editor.workflowInputSchema),
      },
    ];
    for (const n of nodes) {
      const data = (n.data ?? {}) as { label?: unknown };
      const label = typeof data.label === 'string' && data.label.length > 0 ? data.label : n.id;
      list.push({
        sourceKey: n.id,
        label,
        fields: getEffectiveOutputFieldNamesByNodeKey(editor.getNode, n.id, schemaCtx),
      });
    }
    return list;
  }, [nodes, editor]);

  const [inputSchemaQuery, outputContractQuery, dedupSettingsQuery] = useQueries({
    queries: [
      {
        queryKey: ['workflow', workflow.id, 'input-schema'],
        queryFn: () =>
          client.http
            .get(`v1/workflows/${workflow.id}/input-schema`)
            .json<WorkflowInputSchemaResponse>(),
        enabled: workflow.currentVersionId !== null,
      },
      {
        queryKey: ['workflow', workflow.id, 'output-schema'],
        queryFn: () =>
          client.http
            .get(`v1/workflows/${workflow.id}/output-schema`)
            .json<WorkflowOutputSchemaResponse>(),
        enabled: workflow.currentVersionId !== null,
      },
      {
        queryKey: ['workflow', workflow.id, 'dedup-settings'],
        queryFn: () =>
          client.http
            .get(`v1/workflows/${workflow.id}/dedup-settings`)
            .json<WorkflowDedupSettingsResponse>(),
        enabled: workflow.currentVersionId !== null,
      },
    ],
  });

  const [inputSchema, setInputSchema] = useState<Record<string, unknown> | null>(null);
  const [outputSchema, setOutputSchema] = useState<Record<string, unknown> | null>(null);
  const [outputMapping, setOutputMapping] = useState('');
  const [dedupEnabled, setDedupEnabled] = useState(false);
  const [signalKeyTemplate, setSignalKeyTemplate] = useState('');

  // Hydrate local state from the server queries as they resolve.
  useEffect(() => {
    if (!inputSchemaQuery.data) return;
    setInputSchema(
      (inputSchemaQuery.data.inputSchema as Record<string, unknown> | null | undefined) ?? null,
    );
  }, [inputSchemaQuery.data]);
  useEffect(() => {
    if (!outputContractQuery.data) return;
    setOutputSchema(
      (outputContractQuery.data.outputSchema as Record<string, unknown> | null | undefined) ?? null,
    );
    setOutputMapping(outputContractQuery.data.outputMapping ?? '');
  }, [outputContractQuery.data]);
  useEffect(() => {
    if (!dedupSettingsQuery.data) return;
    setDedupEnabled(dedupSettingsQuery.data.dedupEnabled);
    setSignalKeyTemplate(dedupSettingsQuery.data.signalKeyTemplate ?? '');
  }, [dedupSettingsQuery.data]);

  // Live validation of the signal-key template against the trigger
  // input fields the author has declared. Variables that don't resolve to a
  // top-level `inputSchema` property are flagged before save — the same
  // check the API runs (`validateSignalKeyTemplate`), so a bad template
  // never reaches a 400. With no declared inputSchema the trigger shape is
  // unconstrained, so any template is accepted (matches the backend).
  const trimmedTemplate = signalKeyTemplate.trim();
  const inputFieldNames = useMemo(() => topLevelKeys(inputSchema), [inputSchema]);
  const templateVars = useMemo(() => extractSignalKeyVars(trimmedTemplate), [trimmedTemplate]);
  const hasDeclaredInputSchema = inputFieldNames.length > 0;
  const templateValidation = useMemo(
    () =>
      hasDeclaredInputSchema
        ? validateSignalKeyTemplate(trimmedTemplate, inputFieldNames)
        : { ok: true, unknownVars: [] },
    [trimmedTemplate, inputFieldNames, hasDeclaredInputSchema],
  );
  const dedupBlocksSave = dedupEnabled && !templateValidation.ok;

  const save = useMutation({
    mutationFn: () =>
      client.http
        .patch(`v1/workflows/${workflow.id}`, {
          json: {
            inputSchema,
            outputSchema,
            outputMapping: outputMapping.trim() === '' ? null : outputMapping,
            dedupEnabled,
            signalKeyTemplate: trimmedTemplate === '' ? null : trimmedTemplate,
          },
        })
        .json<Workflow>(),
    onSuccess: async () => {
      // Batched invalidation (see workflow-invalidations.ts): three awaited
      // round-trips between the workflow and workflow-versions invalidations
      // left versions.isFetching false while the fresh workflow row joined a
      // stale versions list — the same false "data inconsistency" banner the
      // graph-save path had.
      await invalidateWorkflowQueries(qc, workflow.id, [
        ['workflow', workflow.id, 'input-schema'],
        ['workflow', workflow.id, 'output-schema'],
        ['workflow', workflow.id, 'dedup-settings'],
      ]);
    },
  });

  if (workflow.currentVersionId === null) {
    return (
      <div className="alert alert--info">
        Save a workflow graph first — schemas attach to a workflow version.
      </div>
    );
  }
  if (inputSchemaQuery.isLoading || outputContractQuery.isLoading || dedupSettingsQuery.isLoading)
    return <Spinner />;
  const loadErr = inputSchemaQuery.error ?? outputContractQuery.error ?? dedupSettingsQuery.error;
  if (loadErr)
    return (
      <div className="alert alert--danger">
        Failed to load workflow schemas: {(loadErr as Error).message}
      </div>
    );

  return (
    <div className="card">
      <div
        className="card-body"
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)' }}
      >
        {/* ─── Input contract ─────────────────────────────────────── */}
        <section style={sectionStyle} aria-label="Input schema preview">
          <span className="t-secondary t-mono-label">Fields</span>
          <WorkflowInputSchemaView schema={inputSchemaQuery.data?.inputSchema ?? null} />
        </section>

        <div style={sectionStyle}>
          <label className="t-secondary">
            Inputs
            <InfoHint
              text={
                <>
                  Validates the <code>input</code> body of <code>POST /v1/runs</code> before
                  enqueueing. Violations come back as HTTP 400. Clear for no contract — the workflow
                  accepts any input object.
                </>
              }
            />
          </label>
          <JsonSchemaBuilder value={inputSchema} onChange={setInputSchema} />
        </div>

        {/* ─── Output composition ─────────────────────────────────── */}
        <div style={sectionStyle}>
          <label className="t-secondary">
            Outputs
            <InfoHint
              text={
                <>
                  Compose the workflow output field-by-field. Each row sets one output field — pick
                  a source (workflow trigger or any node) and a field from it. Switch a row to{' '}
                  <em>Advanced</em> for expressions or literals. Clear all rows to fall back to{' '}
                  <em>last terminal node wins</em>.
                </>
              }
            />
          </label>
          <WorkflowOutputMappingEditor
            value={outputMapping}
            onChange={setOutputMapping}
            sources={outputSources}
            availableNodeKeys={nodeKeys}
          />
        </div>

        {/* ─── Output validation ──────────────────────────────────────
            Moved to the bottom of the editor so the form reads top-to-bottom
            as "what the workflow accepts → how its output is composed →
            what the output must satisfy". Last gate, last in the form.
        */}
        <div style={sectionStyle}>
          <label className="t-secondary">
            Output Validation
            <InfoHint
              text={
                <>
                  JSON Schema used to verify that the workflow is producing what we expect — the
                  composed <code>Run.output</code> is validated against this at completion, and a
                  mismatch fails the run with an <code>output-validation</code> error. Clear for no
                  contract.
                </>
              }
            />
          </label>
          <JsonSchemaBuilder value={outputSchema} onChange={setOutputSchema} />
        </div>

        {/* ─── Deduplication (DEV-16225) ──────────────────────────── */}
        <div style={sectionStyle}>
          <label className="t-secondary">
            Deduplication
            <InfoHint
              text={
                <>
                  Stop redundant investigations of the same underlying issue. When enabled, each run
                  gets an <code>signalKey</code> resolved from its trigger input via the template
                  below; the Signal Orchestrator links/cancels runs that share a key with an open
                  signal or an in-flight run. Leave off to run every trigger independently.
                </>
              }
            />
          </label>

          <label
            style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', cursor: 'pointer' }}
          >
            <input
              type="checkbox"
              className="checkbox"
              checked={dedupEnabled}
              onChange={(e) => setDedupEnabled(e.target.checked)}
            />
            <span className="t-sm">Deduplicate runs of this workflow</span>
          </label>

          {dedupEnabled ? (
            <div style={{ ...sectionStyle, marginTop: 'var(--sp-1)' }}>
              <label className="t-secondary t-sm" htmlFor="signal-key-template">
                Signal key template
              </label>
              <input
                id="signal-key-template"
                type="text"
                className="input"
                placeholder="alarm:{deviceId}:{alarmCode}"
                value={signalKeyTemplate}
                onChange={(e) => setSignalKeyTemplate(e.target.value)}
                aria-invalid={dedupBlocksSave}
              />
              {dedupBlocksSave ? (
                <span style={errorTextStyle}>
                  Unknown input field{templateValidation.unknownVars.length > 1 ? 's' : ''}:{' '}
                  {templateValidation.unknownVars.map((v) => `{${v}}`).join(', ')}. Declare{' '}
                  {templateValidation.unknownVars.length > 1 ? 'them' : 'it'} in the Inputs schema
                  above, or remove from the template.
                </span>
              ) : trimmedTemplate === '' ? (
                <span style={helpTextStyle}>
                  No template — runs get no key and aren&apos;t deduplicated until you add one.
                </span>
              ) : (
                <span style={helpTextStyle}>
                  Resolves{' '}
                  {templateVars.length > 0
                    ? templateVars.map((v) => `{${v}}`).join(', ')
                    : 'a static key'}{' '}
                  against each run&apos;s trigger input.
                  {!hasDeclaredInputSchema
                    ? ' No input schema declared — variables can’t be verified ahead of time.'
                    : ''}
                </span>
              )}
            </div>
          ) : null}
        </div>

        {/* ─── Save ───────────────────────────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center' }}>
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={() => save.mutate()}
              disabled={save.isPending || dedupBlocksSave || editor.isReadOnly}
              title={
                editor.isReadOnly
                  ? 'Read-only — viewing a historical version. Promote it to edit.'
                  : dedupBlocksSave
                    ? 'Fix the signal key template before saving.'
                    : undefined
              }
            >
              {save.isPending ? 'Saving…' : 'Save (cut new version)'}
            </button>
            {save.error ? (
              <span style={errorTextStyle}>{(save.error as Error).message}</span>
            ) : null}
          </div>
          {save.isSuccess && !save.isPending ? (
            <span className="t-secondary" style={helpTextStyle}>
              Saved — a new workflow version was cut.
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ─── Workflow output-mapping editor ──────────────────────────────────────
//
// The workflow's `outputMapping` is persisted as a single JSONata
// expression evaluated against `{ __input, <nodeKey>: <output>, ... }`
// at run completion to compose `Run.output`. We author it with the
// same Connect-mode rows UI used for per-node input mappings, by
// round-tripping the JSONata object-construction shape through
// `jsonata-object-mapping`'s compile/parse helpers.
//
// Three modes are possible on load:
//
//   1. Expression is empty / `{}`     → rows mode, no rows yet.
//   2. Expression parses cleanly       → rows mode, rows populated from parse.
//   3. Expression is too complex       → Advanced mode (falls back to the
//                                        existing JSONata textbox so we
//                                        never lossy-edit a complex
//                                        expression). User can flip to
//                                        rows mode at any time by
//                                        clearing first.
//
// We don't try to keep BOTH modes in sync — that's a recipe for losing
// the user's authored expression. Advanced mode owns the string until
// the user explicitly switches to rows (which discards the expression
// and starts fresh).
function WorkflowOutputMappingEditor({
  value,
  onChange,
  sources,
  availableNodeKeys,
}: {
  /** The persisted JSONata expression (or empty string for "no mapping"). */
  value: string;
  /** Replace the persisted expression. */
  onChange: (next: string) => void;
  /** Connect-mode source list (workflow input + each node). */
  sources: IConnectSource[];
  /** All node keys — passed to the Advanced fallback for its ref dropdown. */
  availableNodeKeys: string[];
}) {
  // Try to parse the persisted expression into a record. On success,
  // we own the editor in Connect mode; on null, we fall back to
  // Advanced (the previous JSONata textbox UI). The user can force
  // Advanced even on a parseable expression — same per-row toggle
  // semantics as the input-mapping editor, but at the whole-editor
  // level (since the wire format is a single expression).
  const parsed = useMemo(() => parseJsonataObject(value), [value]);
  const [forceAdvanced, setForceAdvanced] = useState(false);
  const mode: 'rows' | 'advanced' = parsed && !forceAdvanced ? 'rows' : 'advanced';

  // Local row state, so freshly-added rows with empty keys can exist
  // during authoring (the persisted expression can't represent an
  // empty key — `fromMappingRows` drops them on the way out, and
  // without local state the row would vanish the instant it was
  // added).
  const [rows, setRows] = useState<IMappingRow[]>(() => (parsed ? toMappingRows(parsed) : []));
  // Re-sync on external changes to `value` (e.g. server hydration on
  // first load). We key the sync on the raw value to avoid loops.
  useEffect(() => {
    if (parsed) setRows(toMappingRows(parsed));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const updateRows = (next: IMappingRow[]) => {
    setRows(next);
    const record = fromMappingRows(next);
    onChange(compileJsonataObject(record));
  };

  const update = (i: number, patch: Partial<IMappingRow>) => {
    const next = rows.slice();
    const cur = next[i];
    if (!cur) return;
    next[i] = { ...cur, ...patch };
    updateRows(next);
  };
  const remove = (i: number) => updateRows(rows.filter((_, j) => j !== i));
  const add = () => updateRows([...rows, { key: '', value: '' }]);

  // Mode-toggle UI. Mirrors `JsonataMappingBuilder`'s Builder/Raw
  // affordance so the two output-mapping editors feel consistent.
  const cardStyle = {
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-sm)',
    background: 'var(--surface-1, var(--surface-2))',
  };
  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px var(--sp-2)',
    borderBottom: '1px solid var(--border-subtle)',
    background: 'var(--surface-2)',
  };

  return (
    <div style={cardStyle}>
      <div style={headerStyle}>
        <div style={{ display: 'flex', gap: 4 }}>
          <ModePill
            label="Connect"
            active={mode === 'rows'}
            onClick={() => {
              // Going rows ← advanced when the expression isn't parseable
              // would silently drop the user's work — block it with a
              // confirm-on-click. Empty / parseable → free switch.
              if (mode === 'rows') return;
              if (parsed) {
                setForceAdvanced(false);
                return;
              }
              const ok = window.confirm(
                'Your output mapping is a custom expression that can’t be edited in Connect mode. Switching will discard it. Continue?',
              );
              if (!ok) return;
              setForceAdvanced(false);
              onChange('');
              setRows([]);
            }}
          />
          <ModePill
            label="Advanced"
            active={mode === 'advanced'}
            onClick={() => setForceAdvanced(true)}
          />
        </div>
        {mode === 'rows' ? (
          <button
            type="button"
            style={{ ...mappingSmallBtn, borderColor: 'transparent' }}
            onClick={() => {
              setRows([]);
              onChange('');
            }}
            title="Clear all output fields"
          >
            Clear
          </button>
        ) : null}
      </div>

      {mode === 'rows' ? (
        rows.length === 0 ? (
          <div style={{ padding: 'var(--sp-3)' }}>
            <div
              className="t-xs"
              style={{ color: 'var(--text-secondary)', marginBottom: 'var(--sp-2)' }}
            >
              No output fields — the workflow falls back to <em>last terminal node wins</em>.
            </div>
            <button type="button" style={mappingSmallBtn} onClick={add}>
              + Add output field
            </button>
          </div>
        ) : (
          <div>
            {rows.map((r, i) => (
              <MappingRow
                key={i}
                row={r}
                sources={sources}
                onChange={(patch) => update(i, patch)}
                onRemove={() => remove(i)}
                keyPlaceholder="output field"
              />
            ))}
            <div style={{ padding: 'var(--sp-2)' }}>
              <button type="button" style={mappingSmallBtn} onClick={add}>
                + Add output field
              </button>
            </div>
          </div>
        )
      ) : (
        <div style={{ padding: 'var(--sp-2)' }}>
          <JsonataMappingBuilder
            value={value}
            onChange={onChange}
            availableNodeKeys={availableNodeKeys}
          />
        </div>
      )}
    </div>
  );
}

function ModePill({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: active ? 'var(--surface)' : 'transparent',
        color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
        border: '1px solid',
        borderColor: active ? 'var(--border-subtle)' : 'transparent',
        borderRadius: 4,
        padding: '2px 8px',
        fontSize: 11,
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}
